// What SAM accepts as a Display banner for The Trade Desk, asked in the browser
// before a byte leaves it.
//
// The numbers come from `shared/specs/ttd-display-v1.json`, the one catalog the
// Rust side (`shared::ttd_display`) is pinned to, so the editor offers exactly
// the sizes, types and byte limit the server enforces (owner, 2026-10-08: GIF,
// JPEG or PNG; 250,000 bytes, warned past TTD's recommended 150,000; ten sizes). The
// server refuses the same things at
// upload-init (type, bytes) and at attach (the real bytes), so a refusal here is
// the same refusal, said before the upload instead of after it.
//
// `probeDisplayImage` is the browser twin of `shared/src/image_probe.rs`: it
// reads the format, the pixel size and an animation's frames, cycle and plays
// from the file itself, never from `naturalWidth`, so a renamed WebP or an
// EXIF-rotated JPEG is judged by what it is. Both sides run the fixtures in
// `shared/testdata/display/cases.json` (`display.parity.test.ts`).
//
// An animation longer than TTD's 15 seconds is warned about, never refused.

import catalog from '@shared/specs/ttd-display-v1.json';
import type { AssetAnimation } from '@/api/types.gen';

export interface DisplaySize {
  name: string;
  w: number;
  h: number;
}

/** The accepted sizes, in the order the editor offers them. */
export const DISPLAY_SIZES: readonly DisplaySize[] = catalog.sizes.map((size) => ({
  name: size.name,
  w: size.width,
  h: size.height,
}));
export const DISPLAY_MIME_TYPES: readonly string[] = catalog.mime_types;
export const DISPLAY_MAX_BYTES: number = catalog.max_bytes;
/** TTD's "150 KB or smaller": past it a banner is warned about, never refused. */
export const DISPLAY_RECOMMENDED_MAX_BYTES: number = catalog.recommended_max_bytes;
export const DISPLAY_ANIMATION_MAX_MS: number = catalog.animation_max_ms;
export const DISPLAY_POLICY_NOTES: readonly string[] = catalog.policy_notes;
/** For a file input's `accept`: the extensions and the types, so every picker filters. */
export const DISPLAY_ACCEPT = '.gif,.jpg,.jpeg,.png,image/gif,image/jpeg,image/png';
export const DISPLAY_FORMATS_TEXT = 'GIF, JPEG or PNG';

/**
 * Bytes as the server words them: decimal kilobytes, rounded up, so a file
 * 1 byte over the limit never reads as the limit itself beside a refusal.
 */
export function formatDisplayKb(bytes: number): string {
  return `${Math.ceil(bytes / 1000)} KB`;
}

/** "250 KB", SAM's limit. */
export const DISPLAY_MAX_BYTES_TEXT = formatDisplayKb(DISPLAY_MAX_BYTES);
/** "150 KB", TTD's recommended size. */
export const DISPLAY_RECOMMENDED_MAX_BYTES_TEXT = formatDisplayKb(DISPLAY_RECOMMENDED_MAX_BYTES);

/**
 * The note beside a banner over TTD's recommended size, or `null`. A warning
 * only: SAM takes up to `DISPLAY_MAX_BYTES` (owner, 2026-10-08), and TTD's own
 * create accepted far larger files; TTD asks for 150 KB so a banner serves
 * across most inventory sources.
 */
export function displaySizeWarning(bytes: number | bigint | null | undefined): string | null {
  const size = bytes == null ? null : Number(bytes);
  if (size == null || !Number.isFinite(size) || size <= DISPLAY_RECOMMENDED_MAX_BYTES) return null;
  return `${formatDisplayKb(size)}. The Trade Desk recommends ${DISPLAY_RECOMMENDED_MAX_BYTES_TEXT} or smaller so a banner can serve on most sites; some publishers may refuse a larger one.`;
}

export function displaySizeFor(width: number, height: number): DisplaySize | null {
  return DISPLAY_SIZES.find((size) => size.w === width && size.h === height) ?? null;
}

/** The accepted sizes as numbers only: "300×250, 728×90, … or 768×1024". */
export function displaySizesShortText(): string {
  const sizes = DISPLAY_SIZES.map((size) => `${size.w}×${size.h}`);
  return sizes.length > 1 ? `${sizes.slice(0, -1).join(', ')} or ${sizes.at(-1)}` : sizes.join('');
}

// ── The probe ────────────────────────────────────────────────────────────

export type DisplayImageFormat = 'gif' | 'png' | 'jpeg' | 'webp';
export type DisplayProbeError = 'unrecognized' | 'truncated' | 'malformed';

export interface DisplayImageProbe {
  format: DisplayImageFormat;
  width: number;
  height: number;
  animation: AssetAnimation;
}

export type DisplayProbeResult =
  | { ok: true; image: DisplayImageProbe }
  | { ok: false; error: DisplayProbeError; detail?: string };

const FORMAT_NAMES: Record<DisplayImageFormat, string> = {
  gif: 'GIF',
  png: 'PNG',
  jpeg: 'JPEG',
  webp: 'WebP',
};
const FORMAT_MIME: Record<DisplayImageFormat, string> = {
  gif: 'image/gif',
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

const MIME_FORMAT: Record<string, DisplayImageFormat> = Object.fromEntries(
  Object.entries(FORMAT_MIME).map(([format, mime]) => [mime, format as DisplayImageFormat]),
);

/** The format a declared MIME type names, for a sentence; the type itself when unknown. */
function declaredFormatName(mime: string): string {
  const format = MIME_FORMAT[mime];
  return format ? FORMAT_NAMES[format] : mime;
}

const STILL: AssetAnimation = { frame_count: 1, cycle_ms: 0, plays: 1 };
/** Frames counted before an animation is called malformed; real banners have a handful. */
const MAX_FRAMES = 10_000;
/** Browsers play a GIF frame of 0 or 1 hundredths for 10 hundredths (100 ms). */
const GIF_MIN_DELAY_CS = 2;
const GIF_CLAMPED_DELAY_CS = 10;
const U32_MAX = 0xffff_ffff;

class ProbeFailure extends Error {
  constructor(
    readonly kind: DisplayProbeError,
    readonly detail?: string,
  ) {
    super(detail ?? kind);
  }
}

const truncated = () => new ProbeFailure('truncated');
const malformed = (what: string) => new ProbeFailure('malformed', what);

/** A forward-only reader. Every read is bounds-checked and fails `truncated` past the end. */
class Cursor {
  constructor(
    private readonly bytes: Uint8Array,
    private at: number,
  ) {}

  take(n: number): Uint8Array {
    const end = this.at + n;
    if (!Number.isSafeInteger(end) || end > this.bytes.length) throw truncated();
    const slice = this.bytes.subarray(this.at, end);
    this.at = end;
    return slice;
  }

  u8(): number {
    return this.take(1)[0]!;
  }

  u16le(): number {
    const b = this.take(2);
    return b[0]! | (b[1]! << 8);
  }

  u16be(): number {
    const b = this.take(2);
    return (b[0]! << 8) | b[1]!;
  }

  u32be(): number {
    return u32be(this.take(4), 0);
  }

  /** Skip GIF data sub-blocks up to and including the zero-length terminator. */
  skipSubBlocks(): void {
    for (;;) {
      const len = this.u8();
      if (len === 0) return;
      this.take(len);
    }
  }
}

function u32be(b: Uint8Array, at: number): number {
  return ((b[at]! << 24) | (b[at + 1]! << 16) | (b[at + 2]! << 8) | b[at + 3]!) >>> 0;
}

function u32le(b: Uint8Array, at: number): number {
  return (b[at]! | (b[at + 1]! << 8) | (b[at + 2]! << 16) | (b[at + 3]! << 24)) >>> 0;
}

function startsWith(bytes: Uint8Array, prefix: readonly number[], at = 0): boolean {
  if (bytes.length < at + prefix.length) return false;
  return prefix.every((value, index) => bytes[at + index] === value);
}

const ascii = (text: string) => Array.from(text, (ch) => ch.charCodeAt(0));
const equalsAscii = (bytes: Uint8Array, text: string) =>
  bytes.length === text.length && startsWith(bytes, ascii(text));

function dimensions(width: number, height: number): [number, number] {
  if (width === 0 || height === 0) throw malformed('zero width or height');
  return [width, height];
}

/**
 * What an image really is, read from its bytes. Never throws: a file that is
 * not a GIF, PNG, JPEG or WebP, or is cut short, or is broken, answers with
 * the error kind `shared::image_probe::ProbeError` would name.
 */
export function probeDisplayImage(bytes: Uint8Array): DisplayProbeResult {
  try {
    let image: DisplayImageProbe;
    if (startsWith(bytes, ascii('GIF87a')) || startsWith(bytes, ascii('GIF89a'))) {
      image = probeGif(bytes);
    } else if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
      image = probePng(bytes);
    } else if (startsWith(bytes, [0xff, 0xd8])) {
      image = probeJpeg(bytes);
    } else if (bytes.length >= 12 && startsWith(bytes, ascii('RIFF')) && startsWith(bytes, ascii('WEBP'), 8)) {
      image = probeWebp(bytes);
    } else {
      return { ok: false, error: 'unrecognized' };
    }
    return { ok: true, image };
  } catch (error) {
    if (error instanceof ProbeFailure) return { ok: false, error: error.kind, detail: error.detail };
    throw error;
  }
}

function probeGif(bytes: Uint8Array): DisplayImageProbe {
  const cur = new Cursor(bytes, 6);
  const [width, height] = dimensions(cur.u16le(), cur.u16le());
  const packed = cur.u8();
  cur.take(2); // background colour index, pixel aspect ratio
  if (packed & 0x80) cur.take(3 * (1 << ((packed & 0x07) + 1)));
  let frames = 0;
  let cycleCs = 0;
  let pendingDelayCs: number | null = null;
  let loopCount: number | null = null;
  for (;;) {
    const block = cur.u8();
    if (block === 0x3b) break;
    if (block === 0x21) {
      const label = cur.u8();
      if (label === 0xf9) {
        const size = cur.u8();
        if (size < 4) throw malformed('graphic control extension');
        const control = cur.take(size);
        pendingDelayCs = control[1]! | (control[2]! << 8);
        cur.skipSubBlocks();
      } else if (label === 0xff) {
        const size = cur.u8();
        const identifier = cur.take(size);
        const looping = equalsAscii(identifier, 'NETSCAPE2.0') || equalsAscii(identifier, 'ANIMEXTS1.0');
        for (;;) {
          const len = cur.u8();
          if (len === 0) break;
          const data = cur.take(len);
          if (looping && data.length >= 3 && data[0] === 0x01) {
            loopCount = data[1]! | (data[2]! << 8);
          }
        }
      } else {
        cur.skipSubBlocks();
      }
    } else if (block === 0x2c) {
      cur.take(8); // left, top, width, height
      const local = cur.u8();
      if (local & 0x80) cur.take(3 * (1 << ((local & 0x07) + 1)));
      cur.u8(); // LZW minimum code size
      cur.skipSubBlocks();
      frames += 1;
      if (frames > MAX_FRAMES) throw malformed('too many frames');
      const delay = pendingDelayCs ?? 0;
      pendingDelayCs = null;
      cycleCs += delay < GIF_MIN_DELAY_CS ? GIF_CLAMPED_DELAY_CS : delay;
    } else {
      throw malformed('unknown GIF block');
    }
  }
  if (frames === 0) throw malformed('no image in the GIF');
  // No NETSCAPE extension: the animation plays once. `0`: for ever. `n`: browsers
  // play the cycle once and then repeat it `n` more times, so `n + 1` plays — the
  // longer reading, so the 15-second warning is never too lenient.
  const animation: AssetAnimation =
    frames === 1
      ? STILL
      : {
          frame_count: frames,
          cycle_ms: Math.min(cycleCs * 10, U32_MAX),
          plays: loopCount === null ? 1 : loopCount === 0 ? 0 : loopCount + 1,
        };
  return { format: 'gif', width, height, animation };
}

function probePng(bytes: Uint8Array): DisplayImageProbe {
  const cur = new Cursor(bytes, 8);
  const headerLength = cur.u32be();
  if (!equalsAscii(cur.take(4), 'IHDR') || headerLength < 8) {
    throw malformed('PNG does not start with IHDR');
  }
  const header = cur.take(headerLength);
  const [width, height] = dimensions(u32be(header, 0), u32be(header, 4));
  cur.take(4); // CRC
  let control: [number, number] | null = null;
  // Microseconds, rounded up to a whole millisecond once, so fractional delays
  // (1/60 s) never shorten the animation below what it really plays.
  let cycleUs = 0;
  let frameControls = 0;
  for (;;) {
    const length = cur.u32be();
    const kind = cur.take(4);
    const data = cur.take(length);
    cur.take(4); // CRC
    if (equalsAscii(kind, 'IEND')) break;
    if (equalsAscii(kind, 'acTL') && data.length >= 8) {
      control = [u32be(data, 0), u32be(data, 4)];
    } else if (equalsAscii(kind, 'fcTL') && data.length >= 26) {
      frameControls += 1;
      if (frameControls > MAX_FRAMES) throw malformed('too many frames');
      const numerator = (data[20]! << 8) | data[21]!;
      const denominator = ((data[22]! << 8) | data[23]!) || 100;
      cycleUs += Math.floor((numerator * 1_000_000) / denominator);
    }
  }
  const animation: AssetAnimation =
    control && control[0] > 1
      ? {
          frame_count: Math.min(control[0], MAX_FRAMES),
          cycle_ms: Math.min(Math.ceil(cycleUs / 1000), U32_MAX),
          plays: control[1],
        }
      : STILL;
  return { format: 'png', width, height, animation };
}

function probeJpeg(bytes: Uint8Array): DisplayImageProbe {
  const cur = new Cursor(bytes, 2);
  for (;;) {
    if (cur.u8() !== 0xff) throw malformed('JPEG marker expected');
    let marker = cur.u8();
    while (marker === 0xff) marker = cur.u8(); // fill bytes
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
    if (marker === 0xd9 || marker === 0xda) throw malformed('JPEG has no frame header');
    const length = cur.u16be();
    if (length < 2) throw malformed('JPEG segment length');
    const segment = cur.take(length - 2);
    // SOF0..SOF15, except DHT (C4), JPG (C8) and DAC (CC).
    const isFrame =
      marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isFrame) {
      if (segment.length < 5) throw malformed('JPEG frame header');
      const height = (segment[1]! << 8) | segment[2]!;
      const width = (segment[3]! << 8) | segment[4]!;
      const [w, h] = dimensions(width, height);
      return { format: 'jpeg', width: w, height: h, animation: STILL };
    }
  }
}

function probeWebp(bytes: Uint8Array): DisplayImageProbe {
  const cur = new Cursor(bytes, 12);
  const kind = cur.take(4);
  cur.take(4); // chunk size
  let width: number;
  let height: number;
  if (equalsAscii(kind, 'VP8X')) {
    const data = cur.take(10);
    width = (data[4]! | (data[5]! << 8) | (data[6]! << 16)) + 1;
    height = (data[7]! | (data[8]! << 8) | (data[9]! << 16)) + 1;
  } else if (equalsAscii(kind, 'VP8L')) {
    const data = cur.take(5);
    const bits = u32le(data, 1);
    width = (bits & 0x3fff) + 1;
    height = ((bits >>> 14) & 0x3fff) + 1;
  } else if (equalsAscii(kind, 'VP8 ')) {
    const data = cur.take(10);
    width = (data[6]! | (data[7]! << 8)) & 0x3fff;
    height = (data[8]! | (data[9]! << 8)) & 0x3fff;
  } else {
    throw malformed('unknown WebP chunk');
  }
  const [w, h] = dimensions(width, height);
  return { format: 'webp', width: w, height: h, animation: STILL };
}

// ── Animation ────────────────────────────────────────────────────────────

type AnimationLike = Pick<AssetAnimation, 'frame_count' | 'cycle_ms' | 'plays'> | null | undefined;

/** How long it animates before it stops; `null` when it never stops. */
export function displayAnimationTotalMs(animation: AnimationLike): number | null {
  if (!animation || animation.frame_count <= 1) return 0;
  if (animation.plays === 0) return null;
  return animation.cycle_ms * animation.plays;
}

/**
 * TTD's "maximum of 15 seconds of looping, after which they must remain
 * static": it never stops, or it animates longer than 15 s. A still image
 * never breaks it. The same rule as `shared::ttd_display::animation_exceeds_limit`,
 * which sets the review page's `exceeds_animation_limit`.
 */
export function displayAnimationExceedsLimit(animation: AnimationLike): boolean {
  if (!animation || animation.frame_count <= 1) return false;
  const total = displayAnimationTotalMs(animation);
  return total === null || total > DISPLAY_ANIMATION_MAX_MS;
}

/** Rounded UP to a tenth, so a duration past the limit never reads as the limit itself. */
function seconds(ms: number): string {
  const tenths = Math.ceil(ms / 100) / 10;
  return `${Number.isInteger(tenths) ? tenths.toFixed(0) : tenths.toFixed(1)} s`;
}

const ANIMATION_ADVICE = `The Trade Desk asks for ${seconds(DISPLAY_ANIMATION_MAX_MS)} at most, then a still frame; some publishers may refuse it.`;

/**
 * The sentence shown beside an animated banner that breaks the 15-second
 * guidance, or `null` when it does not (a still image, or one that stops in
 * time). A warning only: SAM never refuses an animation.
 */
export function displayAnimationWarning(animation: AnimationLike): string | null {
  if (!displayAnimationExceedsLimit(animation)) return null;
  const total = displayAnimationTotalMs(animation);
  if (total === null) {
    return `Loops forever, ${seconds(animation!.cycle_ms)} per loop. ${ANIMATION_ADVICE}`;
  }
  return `Animates for ${seconds(total)} in total. ${ANIMATION_ADVICE}`;
}

// ── A file, before it is uploaded ────────────────────────────────────────

export interface DisplayFileCheck {
  /** Why the server would refuse it, in the order it would. Empty: acceptable. */
  refusals: string[];
  /** What the bytes say, when they could be read. */
  image: DisplayImageProbe | null;
  /** The catalog size it matches, when it matches one. */
  size: DisplaySize | null;
  /** The animation warning, never a refusal. */
  warning: string | null;
}

/** A file this large is refused on its size alone; its bytes are not read. */
const MAX_PROBED_BYTES = 16 * 1024 * 1024;

async function readBytes(file: Blob): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return new Uint8Array(await file.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Everything upload-init and attach would refuse about a Display file, asked
 * from its bytes: the type (GIF, JPEG or PNG, both as the browser declares it,
 * which upload-init checks, and as the bytes are, which attach checks), the
 * byte limit, and the pixel size. `expected`, when given, is the size the
 * placement already has: a replacement must keep it. `sizesListed` says the screen already lists
 * the accepted sizes beside the upload (the Display builder's size chips), so a wrong size is
 * pointed at that list instead of repeating it.
 */
export async function validateDisplayFile(
  file: File,
  expected?: { w: number; h: number } | null,
  { sizesListed = false }: { sizesListed?: boolean } = {},
): Promise<DisplayFileCheck> {
  const refusals: string[] = [];
  let image: DisplayImageProbe | null = null;
  let unreadable: DisplayProbeError | null = null;
  if (file.size <= MAX_PROBED_BYTES) {
    const result = probeDisplayImage(await readBytes(file));
    if (result.ok) image = result.image;
    else unreadable = result.error;
  }

  if (image && image.format === 'webp') {
    refusals.push(`Display banners must be ${DISPLAY_FORMATS_TEXT}; this file is a WebP image.`);
  } else if (unreadable === 'unrecognized') {
    refusals.push(`Display banners must be ${DISPLAY_FORMATS_TEXT}; this file is not one of them.`);
  } else if (unreadable) {
    refusals.push('This image file is damaged or cut short, so it cannot be read.');
  } else if (!DISPLAY_MIME_TYPES.includes(file.type)) {
    // The bytes may be fine, but upload-init judges the type the browser
    // declares, and refuses anything else.
    const declared = image ? `a ${FORMAT_NAMES[image.format]} image` : file.type || 'of an unknown type';
    refusals.push(
      image && DISPLAY_MIME_TYPES.includes(FORMAT_MIME[image.format])
        ? `Display banners must be ${DISPLAY_FORMATS_TEXT}. This file is ${declared}, but its name does not say so; save it with a .gif, .jpg or .png extension and choose it again.`
        : `Display banners must be ${DISPLAY_FORMATS_TEXT}; this file is ${declared}.`,
    );
  } else if (image && FORMAT_MIME[image.format] !== file.type) {
    // Both are accepted types, but attach compares the bytes with the type the
    // upload declared and refuses a GIF that calls itself a PNG.
    refusals.push(
      `This file is a ${FORMAT_NAMES[image.format]} image, but its name says ${declaredFormatName(file.type)}. Save it with the matching extension and choose it again.`,
    );
  }

  if (file.size > DISPLAY_MAX_BYTES) {
    refusals.push(
      `Display banners must be ${DISPLAY_MAX_BYTES_TEXT} or smaller; this file is ${formatDisplayKb(file.size)}.`,
    );
  }

  const size = image ? displaySizeFor(image.width, image.height) : null;
  if (image && image.format !== 'webp') {
    if (!size) {
      refusals.push(
        `This banner is ${image.width}×${image.height}, which is not a Display size. ${
          sizesListed ? 'Use one of the sizes listed above.' : `Use ${displaySizesShortText()}.`
        }`,
      );
    } else if (expected && (expected.w !== image.width || expected.h !== image.height)) {
      refusals.push(
        `This placement is ${expected.w}×${expected.h}; the new file is ${image.width}×${image.height}. Remove the placement and add a new one for a different size.`,
      );
    }
  }

  return {
    refusals,
    image,
    size,
    warning: image ? displayAnimationWarning(image.animation) : null,
  };
}
